import { NEVER, Observable, catchError, throwError } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { Inject, Service } from "typedi";
import { IMessageBusEvent } from "../../../application/core/models/IMessageBusEvent";
import { type IInternalsMonitor } from "../../../application/core/services/monitoring/IInternalsMonitor";
import { IGooglePaySessionPaymentsClient } from "../../../integrations/google-pay/models/IGooglePaySessionPaymentsClient";
import { IConfig } from "../../../shared/model/config/IConfig";
import { ConfigProvider } from "../../../shared/services/config-provider/ConfigProvider";
import { ResourceLoadingTimeoutHandler } from "../../../shared/services/utils/ResourceLoadingTimeoutHandler";
import { IGooglePaySdkProvider } from "./google-pay-sdk-provider/IGooglePaySdkProvider";
import { GooglePayButton } from "./GooglePayButton";
import { GooglePayButtonDomInjector } from "./GooglePayButtonDomInjector";

@Service()
export class GooglePayClient {
  private destroy$: Observable<IMessageBusEvent>;

  constructor(
    private configProvider: ConfigProvider,
    private googlePaySdkProvider: IGooglePaySdkProvider,
    private googlePayButton: GooglePayButton,
    private googlePayButtonDomInjector: GooglePayButtonDomInjector,
    @Inject("IInternalsMonitor") private internalMonitor: IInternalsMonitor,
    private resourceTimeoutHandler: ResourceLoadingTimeoutHandler,
  ) {
    this.destroy$ = NEVER;
  }

  init(config: IConfig): Observable<IConfig> {
    return this.googlePaySdkProvider
      .setupSdk$(this.configProvider.getConfig())
      .pipe(
        this.resourceTimeoutHandler.captureAndReportResourceLoadingTimeout(
          "Google Pay script load timeout",
        ),
        catchError((error) => {
          return throwError(() => error);
        }),
        tap((googlePaySdk: IGooglePaySessionPaymentsClient) => {
          const button = this.googlePayButton.createButton(
            config,
            googlePaySdk,
          );
          const { buttonRootNode } = config.googlePay.buttonOptions;
          this.googlePayButtonDomInjector.addButtonToDom(
            buttonRootNode,
            button,
          );
        }),
        switchMap(() => this.configProvider.getConfig$()),
      );
  }
}
